import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lightbulb,
  Loader2,
  Mic,
  MicOff,
  RefreshCw,
  Send,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";
import { DIDAvatar, type DIDAvatarHandle } from "./components/DIDAvatar";
import { didStream, DIDInsufficientCreditsError } from "@/lib/did";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/input";
import { useSession } from "@/stores/session";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { speak, cleanForSpeech, stopSpeech, fetchVoiceStatus, setPreferredVoiceKey, unlockAudio, type VoiceEngine } from "@/lib/tts";
import type { ChatMessage } from "@/lib/api";
import { voiceTtsMode } from "@/lib/voices";
import { useWorkspace } from "@/stores/workspace";
import {
  MicRecorder,
  fetchVoiceAgentStatus,
  stopVoiceAgentAudio,
  transcribeAudio,
} from "@/lib/voice-agent";

// Reserved for future Sesame CSM WebSocket voice streaming
// const AVATAR_WS = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/voice/avatar/stream`;

interface Turn {
  id: string;
  role: "user" | "assistant";
  content: string;
  adapter?: string;
  speaking?: boolean;
  // "lora" = real fine-tuned adapter fired, "base" = base model, "offline" = stub
  source?: "lora" | "base" | "offline";
}

const STARTERS = [
  "Teach me algebra basics",
  "Give me a Data Sufficiency warmup",
  "I keep getting Critical Reasoning wrong",
  "Quiz me on rates and work problems",
  "What's my predicted GMAT score?",
];

let _turnId = 0;
const nextId = () => String(++_turnId);

const MAX_HISTORY = 16;

/** Completed turns only — skip empty in-flight assistant bubbles. */
function buildHistory(prior: Turn[], latestUserText: string): ChatMessage[] {
  const completed = prior.filter((t) => t.content.trim());
  const slice = completed.slice(-MAX_HISTORY);
  return [
    ...slice.map((t) => ({
      role: t.role as "user" | "assistant",
      content: t.content,
    })),
    { role: "user", content: latestUserText },
  ];
}

function isFollowUp(turns: Turn[]): boolean {
  return turns.filter((t) => t.content.trim()).length > 1;
}

// ── Route student message to the right LoRA adapter ─────────────────────────
function isLessonRequest(text: string): boolean {
  const t = text.toLowerCase();
  return (
    /teach me|walk me through the basics|from scratch|fundamentals|intro to|introduction to/.test(t) ||
    (/what is|what are|how does/.test(t) && /concept|formula|rule|algebra|geometry|arithmetic|equation/.test(t)) ||
    /basics of|basics in|learn about/.test(t)
  );
}

function shouldExplainLikeTutor(text: string, turns: Turn[]): boolean {
  const t = text.trim();
  // Concept lessons use the lesson adapter, not the problem explainer.
  if (isLessonRequest(t)) return false;
  // Mid-conversation: only run full explain pipeline for a freshly pasted problem.
  if (isFollowUp(turns)) {
    return t.startsWith("{") && (t.includes("question_stem") || t.includes('"stem"'));
  }
  if (t.startsWith("{") && (t.includes("question_stem") || t.includes('"stem"'))) return true;
  if (/explain|walk me through|how do (i|you) solve|solution for|help me (with|solve)/i.test(t)) {
    return true;
  }
  // Word problem: has a question mark and numbers (rates, rowing, etc.)
  if (/\?/.test(t) && /\d/.test(t) && t.length > 40) return true;
  return false;
}

function pickAdapter(text: string, turns: Turn[]): Parameters<typeof api.chat>[0]["adapter"] {
  const t = text.toLowerCase();
  const followUp = isFollowUp(turns);
  const lastAdapter = [...turns].reverse().find((tt) => tt.role === "assistant" && tt.adapter)?.adapter;

  // Follow-ups in an active lesson — stay in concept-explainer until topic shifts.
  if (followUp && lastAdapter === "concept-explainer") {
    if (t.match(/don.t understand|confused|what do you mean|why|how come|not sure|again|clarify|explain that|slower|repeat/)) {
      return "concept-explainer";
    }
    if (t.match(/yes|yeah|yep|ok|okay|got it|thanks|thank you|next|continue|go on|more|show me|keep going|makes sense|i see/)) {
      return "concept-explainer";
    }
    // Student answered a check question (often short numeric or brief reply).
    if (t.length < 40 && !t.match(/give me|generate|score|plan|weak/)) {
      return "concept-explainer";
    }
  }

  // Follow-ups in an active problem discussion.
  if (followUp) {
    if (t.match(/don.t understand|confused|what do you mean|step \d|why is|how come|that makes|not sure|again|clarify/)) {
      return lastAdapter === "explainer" || t.match(/step|explain|why|how|answer/) ? "explainer" : "socratic-tutor";
    }
    if (t.match(/yes|yeah|ok|got it|thanks|thank you|next|continue|go on/)) {
      return lastAdapter === "concept-explainer" ? "concept-explainer" : "socratic-tutor";
    }
  }

  if (t.match(/give me|generate|create|make|write|another question|new question|practice question/)) {
    if (t.match(/verbal|reading|rc|critical reason/)) return "verbal-generator";
    if (t.match(/data sufficiency|ds|data insight|di|two.part/)) return "data-insights-generator";
    return "quant-generator";
  }
  if (t.match(/hint|clue|nudge|stuck|don.t know|lost/)) return "hint-generator";
  if (isLessonRequest(text)) return "concept-explainer";
  if (t.match(/explain|walk me|show me|why is the answer|solution/)) return "explainer";
  if (t.match(/teach me|what is|what are|concept|formula|rule|how does|approach|basics/)) return "concept-explainer";
  if (t.match(/wrong|missed|incorrect|mistake|error|why did i/)) return "mistake-coach";
  if (t.match(/score|predict|how am i doing|my level|progress/)) return "score-predictor";
  if (t.match(/weak|weakness|gap|struggling|bad at|worst|improve/)) return "weakness-detector";
  if (t.match(/plan|schedule|study plan|how long|weeks|roadmap|prepare/)) return "lesson-planner";
  return "socratic-tutor";
}

const SHOW_DEV_META = import.meta.env.DEV;

const MAYA_GREETING =
  "Alright — what are we working on today? You can ask me to teach something from scratch, walk through a problem, or just talk it out. Tap the mic when you're ready.";

const MAYA_ART = "/art/art-maya.png";

export function Tutor() {
  const { voiceEnabled, toggleVoice, preferredVoiceId } = useSession();
  const tutorSessionNonce = useWorkspace((s) => s.tutorSessionNonce);
  const consumeTutorLaunch = useWorkspace((s) => s.consumeTutorLaunch);
  const [activeTopicName, setActiveTopicName] = useState<string | null>(null);
  const handledLaunchRef = useRef(0);
  const lessonSendRef = useRef<(text: string) => Promise<void>>(async () => {});
  // Live D-ID video is opt-in (it connects a slow WebRTC stream + uses credits).
  // Default experience = instant Maya portrait + voice.
  const [liveVideo, setLiveVideo] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([
    {
      id: "0",
      role: "assistant",
      adapter: "socratic-tutor",
      content: MAYA_GREETING,
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [listening, setListening] = useState(false); // enabled after user grants mic
  const [micPermission, setMicPermission] = useState<"prompt" | "granted" | "denied">("prompt");
  const [liveTranscript, setLiveTranscript] = useState("");
  const [avatarReady, setAvatarReady] = useState(false);
  const [avatarSpeaking, setAvatarSpeaking] = useState(false);
  const [didOutOfCredits, setDidOutOfCredits] = useState(false);
  const [voiceEngine, setVoiceEngine] = useState<VoiceEngine | "loading">("loading");
  const [voiceLabel, setVoiceLabel] = useState("Premium voice");
  const [voiceAgentReady, setVoiceAgentReady] = useState(false);
  const useWhisperMic = voiceTtsMode() === "sesame" && voiceAgentReady;
  const [micLevel, setMicLevel] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<DIDAvatarHandle>(null);
  const turnsRef = useRef(turns);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const listeningRef = useRef(false);
  const pausedRef = useRef(false);
  const streamingRef = useRef(false);
  const avatarSpeakingRef = useRef(false);
  const sendMessageRef = useRef<(text: string) => void>(() => {});
  const speechAccumRef = useRef("");
  const speechDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const micRecorderRef = useRef<MicRecorder | null>(null);

  /** Human turn-taking: listening → thinking → speaking */
  const busy = streaming || avatarSpeaking;
  const conversationPhase: "listening" | "thinking" | "speaking" | "idle" =
    avatarSpeaking ? "speaking" : streaming ? "thinking" : listening ? "listening" : "idle";

  // Mirror state to refs so SpeechRecognition callbacks see latest values
  useEffect(() => { turnsRef.current = turns; }, [turns]);
  useEffect(() => { listeningRef.current = listening; }, [listening]);
  useEffect(() => { pausedRef.current = busy; }, [busy]);
  useEffect(() => { streamingRef.current = streaming; }, [streaming]);
  useEffect(() => { avatarSpeakingRef.current = avatarSpeaking; }, [avatarSpeaking]);

  useEffect(() => {
    setPreferredVoiceKey(preferredVoiceId);
  }, [preferredVoiceId]);

  useEffect(() => {
    void Promise.all([fetchVoiceStatus(), fetchVoiceAgentStatus()]).then(
      ([tts, agent]) => {
        setVoiceEngine(tts.engine);
        setVoiceLabel(tts.label);
        setVoiceAgentReady(agent.ready);
      },
    );
  }, []);

  useEffect(() => {
    const el = scrollRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement | null;
    // Newest messages render at the top — keep scroll pinned there.
    if (el) el.scrollTop = 0;
  }, [turns, liveTranscript]);

  // ── Send message → smart adapter → real LLM via RunPod ──────────────────
  const speakMayaReply = useCallback(
    async (spokenText: string) => {
      const useDid =
        liveVideo &&
        avatarReady &&
        didStream.connected &&
        !didOutOfCredits &&
        avatarRef.current;

      const runVoice = () =>
        speak(
          spokenText,
          () => setAvatarSpeaking(true),
          () => setAvatarSpeaking(false),
          preferredVoiceId,
        ).catch((e) => {
          console.warn("TTS failed:", e);
          setAvatarSpeaking(false);
        });

      if (useDid) {
        try {
          setAvatarSpeaking(true);
          await avatarRef.current!.speak(spokenText);
        } catch (err) {
          if (err instanceof DIDInsufficientCreditsError) {
            setDidOutOfCredits(true);
            void didStream.disconnect().catch(() => {});
          }
          setAvatarSpeaking(false);
          await runVoice();
        } finally {
          setAvatarSpeaking(false);
        }
      } else {
        await runVoice();
      }
    },
    [liveVideo, avatarReady, didOutOfCredits, preferredVoiceId],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || streamingRef.current || avatarSpeakingRef.current) return;
      void unlockAudio();
      stopSpeech();
      stopVoiceAgentAudio();
      speechAccumRef.current = "";
      setLiveTranscript("");
      if (speechDebounceRef.current) {
        clearTimeout(speechDebounceRef.current);
        speechDebounceRef.current = null;
      }
      setInput("");
      const userTurn: Turn = { id: nextId(), role: "user", content: text };
      setTurns((t) => [...t, userTurn]);
      setStreaming(true);

      const assistantId = nextId();
      setTurns((t) => [
        ...t,
        { id: assistantId, role: "assistant", content: "", speaking: true },
      ]);

      try {
        const prior = turnsRef.current;
        const adapter = pickAdapter(text, prior);

        // Human tutor pipeline: full step-by-step explain for a NEW problem only.
        if (shouldExplainLikeTutor(text, prior)) {
          const res = await api.tutorExplainText(text);
          const spoken = res.spoken || cleanForSpeech(res.explanation);
          const source: Turn["source"] = res.adapter_used ? "lora" : "base";

          setTurns((t) =>
            t.map((tt) =>
              tt.id === assistantId
                ? {
                    ...tt,
                    content: res.explanation,
                    adapter: "explainer",
                    speaking: true,
                    source,
                  }
                : tt,
            ),
          );

          if (voiceEnabled) {
            await speakMayaReply(spoken);
          }
          return;
        }

        const history = buildHistory(prior, text);
        const res = await api.tutorChat({
          adapter,
          persona: "tutor",
          messages: history,
          max_tokens:
            adapter === "concept-explainer" ? 520 : adapter === "explainer" ? 480 : 360,
          temperature: adapter === "concept-explainer" ? 0.4 : 0.45,
        });

        const source: Turn["source"] =
          res.finish_reason === "stub" ? "offline" : res.adapter_used ? "lora" : "base";
        setTurns((t) =>
          t.map((tt) =>
            tt.id === assistantId
              ? { ...tt, content: res.content, adapter, speaking: true, source }
              : tt,
          ),
        );

        if (voiceEnabled) {
          await speakMayaReply(cleanForSpeech(res.content));
        }
      } catch {
        setTurns((t) =>
          t.map((tt) =>
            tt.id === assistantId
              ? { ...tt, content: "Sorry, something went wrong. Try again.", speaking: false }
              : tt,
          ),
        );
      } finally {
        setStreaming(false);
        setTurns((t) =>
          t.map((tt) =>
            tt.id === assistantId ? { ...tt, speaking: false } : tt,
          ),
        );
      }
    },
    [voiceEnabled, speakMayaReply],
  );

  sendMessageRef.current = sendMessage;
  lessonSendRef.current = sendMessage;

  // Topics tab → auto-start lesson once sendMessage exists (nonce avoids StrictMode double-fire).
  useEffect(() => {
    if (!tutorSessionNonce || handledLaunchRef.current === tutorSessionNonce) return;
    const session = consumeTutorLaunch();
    if (!session) return;
    handledLaunchRef.current = tutorSessionNonce;
    setActiveTopicName(session.topicName);
    setTurns([
      {
        id: nextId(),
        role: "assistant",
        adapter: "concept-explainer",
        content: `Alright — let's work on ${session.topicName}. I'll teach this step by step, the way we would across the table.`,
      },
    ]);
    void lessonSendRef.current(session.lessonPrompt);
  }, [tutorSessionNonce, consumeTutorLaunch]);

  const startMic = useCallback(() => {
    setListening(true);
    listeningRef.current = true;
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.start(); } catch { /* already running */ }
    }
  }, []);

  const flushSpeech = useCallback(() => {
    if (speechDebounceRef.current) {
      clearTimeout(speechDebounceRef.current);
      speechDebounceRef.current = null;
    }
    const toSend = speechAccumRef.current.trim();
    speechAccumRef.current = "";
    setLiveTranscript("");
    if (toSend && !pausedRef.current) sendMessageRef.current(toSend);
  }, []);

  // ── Voice agent mic (Whisper STT) or browser Speech API fallback ─────────
  useEffect(() => {
    const paused = busy;

    if (!listening || paused) {
      micRecorderRef.current?.stop();
      micRecorderRef.current = null;
      try { recognitionRef.current?.stop(); } catch { /* ignore */ }
      if (!listening) setMicLevel(0);
      return;
    }

    // Self-hosted: Whisper on RunPod
    if (useWhisperMic) {
      const rec = new MicRecorder();
      micRecorderRef.current = rec;
      void rec.start({
        silenceMs: 1500,
        onLevel: (l) => setMicLevel(l),
        onUtterance: async (blob) => {
          if (pausedRef.current) return;
          setLiveTranscript("Transcribing…");
          try {
            const text = await transcribeAudio(blob);
            setLiveTranscript("");
            if (text.trim()) sendMessageRef.current(text);
          } catch (e) {
            console.warn("Whisper transcribe failed:", e);
            setLiveTranscript("");
          }
        },
      });
      return () => {
        rec.stop();
        micRecorderRef.current = null;
        setMicLevel(0);
      };
    }

    // Fallback: browser Web Speech API
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rec: any = new SR();
    rec.lang = "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onresult = (e: any) => {
      if (pausedRef.current) return;
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) speechAccumRef.current += `${chunk} `;
        else interim += chunk;
      }
      const preview = (speechAccumRef.current + interim).trim();
      setLiveTranscript(preview);
      if (speechDebounceRef.current) clearTimeout(speechDebounceRef.current);
      speechDebounceRef.current = setTimeout(() => {
        speechDebounceRef.current = null;
        const toSend = speechAccumRef.current.trim();
        speechAccumRef.current = "";
        setLiveTranscript("");
        if (toSend && !pausedRef.current) sendMessageRef.current(toSend);
      }, 1400);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onerror = (e: any) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setMicPermission("denied");
        listeningRef.current = false;
        setListening(false);
      }
    };

    rec.onend = () => {
      if (listeningRef.current && !pausedRef.current) {
        try { rec.start(); } catch { /* already started */ }
      }
    };

    rec.onstart = () => setMicPermission("granted");
    recognitionRef.current = rec;
    try { rec.start(); } catch { /* needs gesture */ }

    return () => {
      if (speechDebounceRef.current) clearTimeout(speechDebounceRef.current);
      rec.onend = null;
      try { rec.stop(); } catch { /* ignore */ }
    };
  }, [listening, useWhisperMic, busy]);

  return (
    <div className="container grid h-[calc(100vh-4rem)] gap-4 py-4 lg:grid-cols-[420px_minmax(0,1fr)] lg:items-start">

      {/* ── LEFT: Avatar panel (fixed size — does not grow with chat) ─ */}
      <div className="flex w-full max-w-[420px] flex-col gap-3 lg:sticky lg:top-4 lg:self-start">
        <div className="flex items-center justify-between">
          <Badge variant="default">
            <Sparkles className="size-3" /> Maya — GMAT AI Tutor
          </Badge>
          <div className="flex gap-1">
            <Button variant={liveVideo ? "secondary" : "outline"} size="sm"
              onClick={() => setLiveVideo((v) => !v)}
              title="Turn on the live lip-synced video avatar (D-ID). Portrait mode is instant + voice-only.">
              {liveVideo ? "● Live lip-sync" : "Enable lip-sync"}
            </Button>
            <Button variant={voiceEnabled ? "secondary" : "outline"} size="icon"
              onClick={() => { void unlockAudio(); toggleVoice(); }}
              title={
                voiceEngine === "elevenlabs-api" || voiceEngine === "elevenlabs-browser"
                  ? "ElevenLabs voice (natural)"
                  : "Browser voice (robotic fallback)"
              }>
              {voiceEnabled ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
            </Button>
          </div>
        </div>
        {voiceEnabled && voiceEngine !== "loading" && (
          <div className="text-[10px] text-muted-foreground">
            Voice:{" "}
            {voiceEngine === "elevenlabs-api" || voiceEngine === "elevenlabs-browser" ? (
              <span className="text-green-600 dark:text-green-400">{voiceLabel}</span>
            ) : voiceEngine === "browser" ? (
              <span className="text-amber-600 dark:text-amber-400">
                Set ELEVENLABS_API_KEY on API for premium voice
              </span>
            ) : (
              <span className="text-muted-foreground">{voiceLabel}</span>
            )}
          </div>
        )}
        {listening && useWhisperMic && micLevel > 0.05 && (
          <div className="h-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-green-500 transition-all duration-75"
              style={{ width: `${Math.min(100, micLevel * 100)}%` }}
            />
          </div>
        )}

        {didOutOfCredits && liveVideo && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
            <strong className="font-medium">Video avatar paused</strong> — D-ID trial credits
            exhausted. Maya is still talking (voice only). Top up at{" "}
            <a
              href="https://studio.d-id.com/account-settings"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-amber-100"
            >
              d-id.com
            </a>{" "}
            to re-enable lip-synced video.
          </div>
        )}

        <div className="relative h-[280px] w-full shrink-0 overflow-hidden rounded-2xl sm:h-[320px] lg:h-[380px] lg:max-h-[380px]">
          {liveVideo && !didOutOfCredits ? (
            <DIDAvatar
              ref={avatarRef}
              sourceUrl={import.meta.env.VITE_DID_AVATAR_URL as string | undefined}
              speaking={avatarSpeaking}
              className="h-full w-full max-h-[380px]"
              onReady={() => setAvatarReady(true)}
              onSpeakStart={() => setAvatarSpeaking(true)}
              onSpeakEnd={() => setAvatarSpeaking(false)}
            />
          ) : (
            /* Instant Maya portrait — fixed frame, no scale with chat */
            <div className="relative h-full w-full overflow-hidden rounded-2xl">
              <img
                src={MAYA_ART}
                alt="Maya, your GMAT tutor"
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
            </div>
          )}
          {avatarSpeaking && (
            <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-xs text-white backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
              </span>
              Maya is speaking…
            </div>
          )}
        </div>

        {/* Quick starters */}
        <div className="flex flex-wrap gap-1.5">
          {STARTERS.map((s) => (
            <button key={s} onClick={() => { void unlockAudio(); sendMessage(s); }} disabled={busy}
              className="rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-xs transition-colors hover:border-primary/50 hover:text-primary disabled:opacity-50">
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* ── RIGHT: Chat + input ──────────────────────────────────────── */}
      <div className="flex min-h-0 flex-col rounded-2xl border border-border/60 bg-card/60 lg:h-[calc(100vh-5rem)]">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <div className="font-display font-semibold">
              {activeTopicName ? `Lesson — ${activeTopicName}` : "Session with Maya"}
            </div>
            <div className="text-xs text-muted-foreground">
              {activeTopicName
                ? "Live tutoring — say continue when you're ready for the next step"
                : conversationPhase === "listening"
                  ? "Your turn — I'm listening"
                  : conversationPhase === "thinking"
                    ? "Give me a second…"
                    : conversationPhase === "speaking"
                      ? "Maya is speaking"
                      : "Tap the mic to start"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" title="Start fresh" disabled={busy} onClick={() => {
              if (busy) return;
              stopSpeech();
              setActiveTopicName(null);
              setTurns([{ id: nextId(), role: "assistant", content: MAYA_GREETING }]);
            }}>
              <RefreshCw className="size-4" />
            </Button>
          </div>
        </div>

        <ScrollArea ref={scrollRef} className="min-h-0 flex-1 px-5">
          <div className="flex flex-col gap-4 py-4">
            {liveTranscript && (
              <div className="flex justify-end">
                <div className="max-w-[88%] rounded-2xl rounded-br-md border border-dashed border-primary/40 bg-primary/5 px-4 py-2.5 text-sm italic text-muted-foreground">
                  {liveTranscript}…
                </div>
              </div>
            )}
            <AnimatePresence initial={false}>
              {[...turns].reverse().map((t) => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={cn("flex flex-col gap-1", t.role === "user" ? "items-end" : "items-start")}>
                  <div className={cn(
                    "max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed",
                    t.role === "user"
                      ? "rounded-br-md bg-gradient-to-br from-primary to-accent text-primary-foreground"
                      : "rounded-bl-md border border-border/60 bg-background/60",
                  )}>
                    {t.content || (
                      <span className="inline-flex gap-1 text-muted-foreground">
                        <span className="animate-pulse">•</span>
                        <span className="animate-pulse [animation-delay:120ms]">•</span>
                        <span className="animate-pulse [animation-delay:240ms]">•</span>
                      </span>
                    )}
                  </div>
                  {SHOW_DEV_META && t.role === "assistant" && t.adapter && (
                    <div className="flex items-center gap-1.5 opacity-50">
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {t.adapter}
                      </Badge>
                      {t.source && (
                        <span className="text-[9px] text-muted-foreground">{t.source}</span>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </ScrollArea>

        {busy && (
          <div
            role="status"
            aria-live="polite"
            className="mx-4 flex items-center gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100"
          >
            <Loader2 className="size-4 shrink-0 animate-spin text-amber-600 dark:text-amber-400" />
            <span>
              {streaming
                ? "Maya is thinking — please wait before asking another question"
                : "Maya is speaking — please wait until she finishes"}
            </span>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (busy) return;
            if (liveTranscript.trim()) flushSpeech();
            else sendMessage(input);
          }}
          className={cn("border-t border-border/60 p-4", busy && "opacity-80")}
        >
          <div className="relative">
            <Textarea
              value={liveTranscript || input}
              onChange={(e) => {
                if (liveTranscript || busy) return;
                setInput(e.target.value);
              }}
              readOnly={!!liveTranscript || busy}
              disabled={busy}
              placeholder={
                busy
                  ? streaming
                    ? "Maya is thinking…"
                    : "Maya is speaking…"
                  : listening
                    ? "Listening…"
                    : "Talk to Maya — ask a question or say 'teach me algebra basics'"
              }
              className={cn(
                "min-h-[72px] resize-none pr-32",
                liveTranscript && "italic text-muted-foreground",
              )}
              onKeyDown={(e) => {
                if (busy) return;
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  if (liveTranscript) flushSpeech();
                  else sendMessage(input);
                }
              }}
            />
            <div className="absolute bottom-2 right-2 flex items-center gap-1">
              <Button
                type="button"
                variant={listening ? "secondary" : "outline"}
                size="icon"
                disabled={busy}
                title={busy ? "Wait for Maya to finish" : listening ? "Mute mic" : "Start voice conversation"}
                onClick={() => {
                  if (busy) return;
                  if (listening) {
                    setListening(false);
                    listeningRef.current = false;
                    if (speechDebounceRef.current) clearTimeout(speechDebounceRef.current);
                    speechAccumRef.current = "";
                    setLiveTranscript("");
                  } else {
                    startMic();
                  }
                }}
              >
                {listening ? (
                  <Mic className={cn("size-4", !busy && "text-green-500")} />
                ) : (
                  <MicOff className="size-4 text-muted-foreground" />
                )}
              </Button>
              <Button type="button" variant="ghost" size="icon" title="Get a hint"
                onClick={() => sendMessage("Give me a hint, don't reveal the answer")}
                disabled={busy}>
                <Lightbulb className="size-4" />
              </Button>
              <Button
                type="submit"
                variant="gradient"
                size="sm"
                disabled={
                  busy ||
                  (!input.trim() && !liveTranscript.trim())
                }
                onClick={(e) => {
                  if (liveTranscript.trim()) {
                    e.preventDefault();
                    flushSpeech();
                  }
                }}
              >
                <Send className="size-3.5" /> {liveTranscript ? "Send voice" : "Send"}
              </Button>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              {micPermission === "denied" ? (
                <span className="text-amber-500">Mic blocked — allow microphone in browser settings</span>
              ) : conversationPhase === "listening" ? (
                <>
                  <span className="mr-1.5 inline-block size-1.5 animate-pulse rounded-full bg-green-500" />
                  Listening — speak naturally, pause when done
                </>
              ) : conversationPhase === "thinking" ? (
                <>
                  <span className="mr-1.5 inline-block size-1.5 rounded-full bg-amber-400" />
                  One moment…
                </>
              ) : conversationPhase === "speaking" ? (
                <>
                  <span className="mr-1.5 inline-block size-1.5 rounded-full bg-amber-400" />
                  Maya is speaking — hold on before you reply
                </>
              ) : (
                "Mic to talk · type to reply · ⌘+↵ to send"
              )}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
